import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { useLocalizedPath } from "../hooks/useLocalizedPath";

const CookieBanner = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const { t } = useTranslation();
  const lp = useLocalizedPath();

  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true,
    functional: false,
    analytics: false,
    performance: false,
  });

  useEffect(() => {
    const savedPreferences = localStorage.getItem("cookiePreferences");
    if (!savedPreferences) {
      const timer = setTimeout(() => {
        setShowBanner(true);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCookiePreferences(JSON.parse(savedPreferences));
    }
  }, []);

  const handleToggle = (category) => {
    if (category === "necessary") return;
    setCookiePreferences((prev) => ({
      ...prev,
      [category]: !prev[category],
    }));
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      functional: true,
      analytics: true,
      performance: true,
    };
    setCookiePreferences(allAccepted);
    localStorage.setItem("cookiePreferences", JSON.stringify(allAccepted));
    initializeGoogleAnalytics();
    setShowBanner(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem(
      "cookiePreferences",
      JSON.stringify(cookiePreferences)
    );
    if (cookiePreferences.analytics) {
      initializeGoogleAnalytics();
    }
    setShowBanner(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
    };
    setCookiePreferences(onlyNecessary);
    localStorage.setItem("cookiePreferences", JSON.stringify(onlyNecessary));
    setShowBanner(false);
  };

  const initializeGoogleAnalytics = () => {
    if (document.getElementById("ga-gtag-script")) return;
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${process.env.REACT_APP_GA_MEASUREMENT_ID}`;
    script.id = "ga-gtag-script";
    document.head.appendChild(script);

    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;
    gtag("js", new Date());
    gtag("config", process.env.REACT_APP_GA_MEASUREMENT_ID);
  };

  if (!showBanner) return null;

  return (
    <div className="cookie-banner">
      <div className="cookie-content">
        {!showDetails ? (
          <>
            <h2 className="cookie-title">{t("CookieTitle")}</h2>
            <p>
              {t("WeUseCookies")}
              <Link to={lp("/cookie-policy")} className="cookie-link">
                {t("Cookie Policy")}.
              </Link>
            </p>
            <div className="cookie-buttons">
              <button
                onClick={() => setShowDetails(true)}
                className="secondary-button"
              >
                {t("CookiePreferences")}
              </button>
              <button onClick={handleAcceptAll} className="primary-button">
                {t("AcceptAll")}
              </button>
              <button onClick={handleRejectAll} className="secondary-button">
                {t("RejectAll")}
              </button>
            </div>
          </>
        ) : (
          <div className="cookie-details">
            <h2>{t("CookiePreferences")}</h2>
            <div className="cookie-categories">
              <div className="cookie-category">
                <div className="category-header">
                  <span>{t("Necessary")}</span>
                  <span className="category-description">
                    {t("EssentialCookiesDesc")}
                  </span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={cookiePreferences.necessary}
                    disabled
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <span>{t("Functional")}</span>
                  <span className="category-description">
                    {t("FunctionalCookiesDesc")}
                  </span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={cookiePreferences.functional}
                    onChange={() => handleToggle("functional")}
                  />
                  <span className="slider"></span>
                </label>
              </div>

              <div className="cookie-category">
                <div className="category-header">
                  <span>{t("Analytics")}</span>
                  <span className="category-description">
                    {t("AnalyticsCookiesDesc")}
                  </span>
                </div>
                <label className="switch">
                  <input
                    type="checkbox"
                    checked={cookiePreferences.analytics}
                    onChange={() => handleToggle("analytics")}
                  />
                  <span className="slider"></span>
                </label>
              </div>
            </div>

            <div className="cookie-buttons">
              <button
                onClick={handleSavePreferences}
                className="primary-button"
              >
                {t("SavePreferences")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;
